import { useState, useCallback } from 'react';
import type { ProblemType, BankProblem, FeedbackType } from '../types';
import type { FractionFocus } from '../components/common/FractionInput';
import { pickProblem, checkNumberAnswer, checkFractionAnswer } from '../lib/bank';
import { REQUIRED_WINS, STAGE_ORDER } from '../constants';

interface UseGameOptions {
  onWin: (category: ProblemType) => void;
  onLevelUp: (nextCategory: ProblemType | null) => void;
}

export function useGame({ onWin, onLevelUp }: UseGameOptions) {
  const [currentProblem, setCurrentProblem] = useState<BankProblem | null>(null);
  const [input, setInput] = useState('');
  const [feedback, setFeedback] = useState<FeedbackType>('NONE');
  const [showHint, setShowHint] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<ProblemType | null>(null);
  const [sessionWins, setSessionWins] = useState(0);

  // Fraction input state
  const [fractionNumerator, setFractionNumerator] = useState('');
  const [fractionDenominator, setFractionDenominator] = useState('');
  const [fractionFocus, setFractionFocus] = useState<FractionFocus>('denominator');

  const answer = currentProblem?.answer;
  const isFractionProblem = answer?.kind === 'fraction';
  const showDecimal = answer?.kind === 'number' && !!answer.isDecimal;
  const hasDecimal = input.includes('.');

  const resetInputs = () => {
    setInput('');
    setFractionNumerator('');
    setFractionDenominator('');
    setFractionFocus('denominator');
  };

  // Pick a fresh problem, avoiding an immediate repeat of the current one.
  const nextProblem = useCallback((type: ProblemType, prev?: BankProblem | null) => {
    setCurrentProblem(pickProblem(type, prev?.id));
  }, []);

  const startGame = useCallback((type: ProblemType) => {
    setSelectedCategory(type);
    setCurrentProblem(pickProblem(type));
    resetInputs();
    setShowHint(false);
    setFeedback('NONE');
    setSessionWins(0);
  }, []);

  const exitGame = useCallback(() => {
    setSelectedCategory(null);
    setCurrentProblem(null);
    resetInputs();
    setShowHint(false);
    setFeedback('NONE');
    setSessionWins(0);
  }, []);

  const handleInput = useCallback((n: number) => {
    if (isFractionProblem) {
      if (fractionFocus === 'numerator') {
        setFractionNumerator((prev) => (prev.length >= 3 ? prev : prev + n));
      } else {
        setFractionDenominator((prev) => (prev.length >= 3 ? prev : prev + n));
      }
    } else {
      setInput((prev) => {
        const maxLen = prev.includes('.') || showDecimal ? 8 : 6;
        return prev.length >= maxLen ? prev : prev + n;
      });
    }
  }, [isFractionProblem, fractionFocus, showDecimal]);

  const handleDecimal = useCallback(() => {
    setInput((prev) => {
      if (prev.includes('.')) return prev;
      if (prev === '') return '0.';
      return prev + '.';
    });
  }, []);

  const clearInput = useCallback(() => {
    if (isFractionProblem) {
      if (fractionFocus === 'numerator') setFractionNumerator('');
      else setFractionDenominator('');
    } else {
      setInput('');
    }
  }, [isFractionProblem, fractionFocus]);

  const handleFractionFocusChange = useCallback((focus: FractionFocus) => {
    setFractionFocus(focus);
  }, []);

  const checkAnswer = useCallback(() => {
    if (!currentProblem || !selectedCategory) return;

    let isCorrect = false;
    if (isFractionProblem) {
      if (fractionNumerator === '' || fractionDenominator === '') return;
      const num = parseInt(fractionNumerator, 10);
      const den = parseInt(fractionDenominator, 10);
      if (isNaN(num) || isNaN(den) || den === 0) return;
      isCorrect = checkFractionAnswer(num, den, currentProblem.answer);
    } else {
      if (input === '') return;
      isCorrect = checkNumberAnswer(input, currentProblem.answer);
    }

    if (isCorrect) {
      const newWins = sessionWins + 1;
      setSessionWins(newWins);
      onWin(selectedCategory);

      const required = REQUIRED_WINS[selectedCategory];
      if (newWins >= required) {
        setFeedback('LEVEL_UP');
        const idx = STAGE_ORDER.indexOf(selectedCategory);
        const next = idx < STAGE_ORDER.length - 1 ? STAGE_ORDER[idx + 1] : null;
        setTimeout(() => {
          onLevelUp(next);
          if (next) startGame(next);
          else {
            setFeedback('NONE');
            startGame(selectedCategory);
          }
        }, 2500);
      } else {
        setFeedback('CORRECT');
        setTimeout(() => {
          setFeedback('NONE');
          nextProblem(selectedCategory, currentProblem);
          resetInputs();
          setShowHint(false);
        }, 1000);
      }
    } else {
      // 学習第一: まちがえても 進捗（れんしゅう数）は 0 に もどさない。
      // かわりに ヒントを 出して もう一度 同じ問題に ちょうせんさせる。
      setFeedback('INCORRECT');
      setTimeout(() => {
        setFeedback('NONE');
        resetInputs();
        setShowHint(true);
      }, 1000);
    }
  }, [
    currentProblem, selectedCategory, input, fractionNumerator, fractionDenominator,
    isFractionProblem, sessionWins, onWin, onLevelUp, startGame, nextProblem,
  ]);

  return {
    currentProblem,
    input,
    feedback,
    showHint,
    selectedCategory,
    sessionWins,
    showDecimal: Boolean(showDecimal),
    hasDecimal,
    isFractionProblem: Boolean(isFractionProblem),
    fractionNumerator,
    fractionDenominator,
    fractionFocus,
    startGame,
    exitGame,
    handleInput,
    handleDecimal,
    clearInput,
    checkAnswer,
    handleFractionFocusChange,
  };
}
